import { replaceEmptyStringsWithUndefined } from "@validereinc/utilities";
import React, { BaseSyntheticEvent, CSSProperties, ReactNode } from "react";
import { FormProvider, SubmitHandler, useFormContext } from "react-hook-form";

import type { FieldValues, UseFormReturn } from "react-hook-form";

const FormContainer = <T extends FieldValues>({
  children,
  onSubmit,
  onBlur,
  shouldPersistClearedInputs,
  className,
  style,
}: FormContainerType<T>) => {
  const { handleSubmit } = useFormContext<T>();

  const onFormSubmit = async (values: T, event?: BaseSyntheticEvent) => {
    if (onBlur) {
      return await onBlur(values, event);
    }

    if (onSubmit) {
      const newFormValues = shouldPersistClearedInputs
        ? values
        : replaceEmptyStringsWithUndefined(values);
      return await onSubmit(newFormValues, event);
    }

    return;
  };

  return (
    <form
      className={className}
      style={style}
      {...(onSubmit && { onSubmit: handleSubmit(onFormSubmit) })}
      {...(onBlur && { onBlur: handleSubmit(onFormSubmit) })}
    >
      {children}
    </form>
  );
};

const Form = <T extends FieldValues>({
  onSubmit = () => null,
  onBlur,
  children,
  isWrappedInFormContainer = true,
  shouldPersistClearedInputs = true, // TODO: make false the default (Requires a lot of QA) https://github.com/ValidereInc/validere-frontend/pull/799#pullrequestreview-1563218603
  className,
  style,
  ...props
}: FormType<T>) => {
  return (
    <FormProvider {...props}>
      {isWrappedInFormContainer ? (
        <FormContainer
          {...{
            onSubmit,
            onBlur,
            shouldPersistClearedInputs,
            className,
            style,
          }}
        >
          {children}
        </FormContainer>
      ) : (
        children
      )}
    </FormProvider>
  );
};

export type FormContainerType<T extends FieldValues> = {
  onSubmit?: SubmitHandler<T>;
  onBlur?: SubmitHandler<T>;
  children: ReactNode;
  shouldPersistClearedInputs?: boolean;
  className?: string;
  style?: CSSProperties;
};

export type FormType<T extends FieldValues> = {
  /** The function called when all the values of the form if validation passes and form submits */
  onSubmit?: SubmitHandler<T>;
  /** function called after an onBlur event occurs */
  onBlur?: SubmitHandler<T>;
  defaultValues?: T;
  isWrappedInFormContainer?: boolean;
  className?: string;
  style?: CSSProperties | undefined;
  /** If false, removes all empty string values before sending to backend */
  shouldPersistClearedInputs?: boolean;
  /** The Form inputs that would have access to the `useFormContext()` hook */
  children: ReactNode;
} & UseFormReturn<T>;

export default Form;
